import React, { useEffect, useState } from 'react';
import { Close } from '../../../components/Glyph';
import { dayShortAndClock } from '../../../lib/when';
import { supabase } from '../../../lib/supabase';
import PageBar from '../../../components/PageBar';
import { useAuth } from '../../../components/AuthContext';
import { parsePastedText, parseClientFile, annotatePastedText } from '../../../lib/clientOrderParse';
import { buildTransferOrderCsv } from '../../../lib/orderParse';
import { fetchAll } from '../../../lib/save';
import ListGrid from '../../../components/ListGrid';
import EmptyScreen from '../../../components/EmptyScreen';
import { Figure, Figures } from '../../../components/Figures';
import { cellInputStyle, CELL_CLASS } from '../Movement/movementUi';
import PageLoading from '../../../components/PageLoading';
import { OnHandIcon } from '../../../components/brand/OnHandLogo';

// Client Ordering, capture orders clients send in messy formats (pasted email
// text, Excel, CSV), standardize them into clean line items, match to the
// catalog, then save and export. A built-in heuristic parser does the
// standardization today; an AI path can be added later behind the same shape.

const C = {
  ink: 'var(--text-primary)', sub: 'var(--text-secondary)', dim: 'var(--text-muted)', line: 'var(--border-soft)',
  blue: 'var(--info-ink)', blueBg: 'var(--info-bg)', blueLine: 'var(--info-bg)',
  okBg: 'var(--success-bg)', okLine: 'var(--success-bg)', okInk: 'var(--success-ink)',
  warnInk: 'var(--warning-ink)', errInk: 'var(--critical-ink)',
};

const CLIENT_TYPES = [
  { key: 'external',        label: 'External customer', entity: null },
  { key: 'referral_source', label: 'Referral source',   entity: 'referral_sources' },
  { key: 'account',         label: 'Account / location', entity: 'accounts' },
  { key: 'warehouse',       label: 'Warehouse',          entity: 'warehouses' },
];

const STATUS_COLORS = {
  draft:     { c: 'var(--text-secondary)', bg: 'var(--hover)' },
  submitted: { c: 'var(--info-ink)', bg: 'var(--info-bg)' },
  fulfilled: { c: 'var(--success-ink)', bg: 'var(--success-bg)' },
  cancelled: { c: 'var(--critical-ink)', bg: 'var(--critical-bg)' },
};

let LOCAL_ID = 1;
const newLine = (partial = {}) => ({
  _id: LOCAL_ID++, description: '', quantity: 1, item_number: '', item_id: null,
  vendor: '', match_status: 'unmatched', raw_text: '', client_text: '', ...partial,
});

// Pick the best catalog candidate for a description by word-overlap score.
function pickBest(description, candidates) {
  const want = new Set(description.toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length >= 3));
  let best = null, bestScore = -1;
  for (const cand of candidates) {
    const have = (cand.description || '').toLowerCase().split(/[^a-z0-9]+/);
    const score = have.reduce((n, w) => n + (want.has(w) ? 1 : 0), 0);
    if (score > bestScore) { bestScore = score; best = cand; }
  }
  return best;
}

export default function ClientOrderingPage() {
  const { profile } = useAuth();
  const [tab, setTab] = useState('new');           // 'new' | 'orders'

  // entity option lists for the client dropdowns
  const [entities, setEntities] = useState({ referral_sources: [], accounts: [], warehouses: [] });

  // new-order form state
  const [clientType, setClientType] = useState('external');
  const [clientName, setClientName] = useState('');
  const [entityId, setEntityId] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [rawText, setRawText] = useState('');
  const [fileName, setFileName] = useState('');
  const [lines, setLines] = useState([]);

  /*
    **A search and two filters, all three on the approved drawing and none of
    them built.** Found 8 September 2026 by laying the running list beside
    `orders-list-1440.png`. Three orders make them look idle; a year of a client
    ordering every fortnight does not, and this list only grows.
  */
  const [orderSearch, setOrderSearch] = useState('');
  const [orderKind, setOrderKind] = useState('all');
  const [orderWhen, setOrderWhen] = useState('all');

  /*
    **What the client actually sent, line by line**, so the screen can grey the
    lines that did not become order lines and say so. Group G's drawing, and the
    thing that found the parser keeping a sign-off as an item.
  */
  const read = lines.length && rawText.trim() ? annotatePastedText(rawText) : [];

  const unitsTotal = lines.reduce((n, l) => n + (parseInt(l.quantity, 10) || 0), 0);
  const [matching, setMatching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);            // { tone, text }

  // Transfer Order export details (fills the order-level columns)
  const [sourceWh, setSourceWh] = useState('');
  const [destWh, setDestWh] = useState('');

  // orders list
  const [orders, setOrders] = useState([]);

  /*
    **The orders this list is actually showing**, after the search and the two
    filters the drawing puts on it.

    **It lived twenty lines above `orders` itself for one build**, which is a
    reference before the thing exists, and a page that does that stops dead with
    nothing on it. The picture was a blank grey rectangle and the build said
    "Compiled successfully". Sixth time on this project.
  */
  const WITHIN = { 30: 'The last 30 days', 90: 'The last 3 months', 365: 'The last year' };
  const shownOrders = orders.filter(o => {
    if (orderKind !== 'all' && o.client_type !== orderKind) return false;
    if (orderWhen !== 'all') {
      const days = (Date.now() - new Date(o.created_at).getTime()) / 86400000;
      if (days > Number(orderWhen)) return false;
    }
    const q = orderSearch.trim().toUpperCase();
    if (!q) return true;
    return (o.client_name || '').toUpperCase().includes(q);
  });
  const orderKinds = [...new Set(orders.map(o => o.client_type).filter(Boolean))];
  const [loadingOrders, setLoadingOrders] = useState(false);
  /*
    **One saved order, opened.** No row on this list opened until 8 September
    2026, so an order could be saved and never read again: the lines, what each
    one matched to, and what the client actually sent were all written down and
    unreachable.
  */
  const [openOrder, setOpenOrder] = useState(null);   // { order, lines }

  useEffect(() => { loadEntities(); }, []);
  useEffect(() => { if (tab === 'orders') { loadOrders(); setOpenOrder(null); } }, [tab]);

  async function loadEntities() {
    const [rs, ac, wh] = await Promise.all([
      supabase.from('referral_sources').select('id, name, display_name').order('name'),
      supabase.from('accounts').select('id, name').order('name'),
      /*
        **Active locations only**, corrected 8 September 2026. The chip was
        counting all 320 including the ones switched off and the ones marked do
        not use, so it offered a person 40 places nothing is ever sent to. The
        approved drawing says 280, which is the active count, and it is what
        every other screen in OnHand means by a location.
      */
      supabase.from('warehouses').select('id, name, display_name')
        .eq('is_inactive', false).eq('is_do_not_use', false).order('name'),
    ]);
    setEntities({
      referral_sources: rs.data || [],
      accounts: ac.data || [],
      warehouses: wh.data || [],
    });
  }

  async function loadOrders() {
    setLoadingOrders(true);
    // **Every order, a page at a time** (the audit after the rebrand, issue 22,
    // 18 September 2026). It stopped at the newest 100 and the heading still
    // said "100 orders", as though that were all of them.
    const { data } = await fetchAll(() => supabase
      .from('client_orders')
      .select('id, client_name, client_type, status, source_format, created_at, client_order_lines(count)')
      .order('created_at', { ascending: false }).order('id'));
    setOrders(data || []);
    setLoadingOrders(false);
  }

  async function openSavedOrder(id) {
    setLoadingOrders(true);
    const [{ data: order }, { data: orderLines }] = await Promise.all([
      supabase.from('client_orders').select('*').eq('id', id).maybeSingle(),
      fetchAll(() => supabase.from('client_order_lines')
        .select('id, item_number, description, raw_text, quantity, match_status')
        .eq('client_order_id', id).order('id')),
    ]);
    setLoadingOrders(false);
    if (!order) { setMsg({ tone: 'err', text: 'That order could not be opened.' }); return; }
    setOpenOrder({ order, lines: orderLines || [] });
  }

  const activeType = CLIENT_TYPES.find(t => t.key === clientType);

  /*
    **Which of the four can find anything, said in one sentence.** Rule 9's
    shape: a list with nothing in it and a list nobody has loaded read the same
    way unless somebody says which is which. Naming what would fill it is the
    part that turns a dead end into a next step.
  */
  const emptyTypeNote = (() => {
    const WHAT_FILLS = {
      referral_sources: 'A referring practice can be picked once the referral list has anything in it.',
      accounts: 'An account can be picked once the account list has anything in it.',
      warehouses: 'A location can be picked once there are active locations.',
    };
    const empty = CLIENT_TYPES
      .filter(t => t.entity && (entities[t.entity] || []).length === 0)
      .map(t => WHAT_FILLS[t.entity]);
    if (!empty.length) return null;
    return empty.join(' ') + ' '
      + (empty.length === 1 ? 'It is empty today.' : 'Both are empty today.');
  })();

  function onPickEntity(id) {
    setEntityId(id);
    const list = entities[activeType.entity] || [];
    const found = list.find(e => e.id === id);
    if (found) setClientName(found.display_name || found.name);
  }

  // Clients usually send item numbers, so treat each parsed line's text (and its
  // first token, or the file's own item-number column) as a candidate item
  // number and resolve it against the catalog, pulling the system Description
  // and vendor. One batched query for the whole order.
  async function autoResolveItemNumbers(rows) {
    const cand = rows.map(l => {
      const fromCol = (l.item_number || '').trim();
      const t = (l.client_text || l.description || '').trim();
      return { explicit: fromCol, full: t, first: t.split(/\s+/)[0] || '' };
    });
    const uniq = Array.from(new Set(cand.flatMap(c => [c.explicit, c.full, c.first]).filter(Boolean)));
    if (!uniq.length) return rows;
    const map = new Map();
    for (let i = 0; i < uniq.length; i += 300) {
      const { data } = await supabase
        .from('items')
        .select('id, item_number, description, vendor')
        .in('item_number', uniq.slice(i, i + 300));
      (data || []).forEach(it => { if (!map.has(it.item_number)) map.set(it.item_number, it); });
    }
    return rows.map((l, i) => {
      const it = map.get(cand[i].explicit) || map.get(cand[i].full) || map.get(cand[i].first);
      if (!it) return l;
      return { ...l, item_id: it.id, item_number: it.item_number, description: it.description || '', vendor: it.vendor || '', match_status: 'matched' };
    });
  }

  /*
    **Reading an order looks its lines up as well.**

    Until 8 September 2026 it matched only on an item number the client had
    written, which almost none of them do, and then said "matched 0 to the
    catalog" and left every line marked No match in red. Looking them up by
    description was a button somebody had to know to press, halfway down a card
    heading. So the ordinary state of every order read from an email was four
    red lines and nothing to suggest what to do about it.

    **A guess is still marked as a guess.** A description match comes back as
    Verify rather than Matched, which is what it is: "Walking Boot" found
    something with those words in it, and a person decides whether it is the
    right one.

    **And the green banner is gone.** It said "Standardized 4 line(s) from
    pasted text; matched 0 to the catalog by item number. Review below." over a
    card that now says "4 lines, 10 units, from a pasted email". Two counts of
    the same thing, one of them in a word nobody outside a database uses.
  */
  function afterStandardize(rows, label) {
    if (!rows.length) {
      setMsg({ tone: 'warn', text: 'Nothing in ' + label + ' looks like an order line. '
        + 'Check it is the list itself rather than the message it came in.' });
      return;
    }
    setMsg(null);
    setLines(rows);
    autoResolveItemNumbers(rows).then(resolved => {
      setLines(resolved);
      lookUpByDescription(resolved);
    });
  }

  function standardizeText() {
    const parsed = parsePastedText(rawText).map(p => newLine({ ...p, client_text: p.description }));
    afterStandardize(parsed, 'pasted text');
  }

  async function onFile(file) {
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const { lines: parsed, location } = parseClientFile(file.name, buf);
      if (location && !clientName.trim()) setClientName(location);  // suggest client from the form's Location
      const mapped = parsed.map(p => newLine({ ...p, client_text: p.description }));
      afterStandardize(mapped, file.name);
    } catch (e) {
      setMsg({ tone: 'err', text: 'Could not read file: ' + e.message });
    }
  }

  function updateLine(id, patch) {
    setLines(ls => ls.map(l => (l._id === id ? { ...l, ...patch } : l)));
  }
  // When an item number is typed in manually, pull its description + vendor from
  // the catalog so the export's Description always comes from the system.
  async function resolveItemNumber(id, num) {
    const n = (num || '').trim();
    if (!n) return;
    const { data } = await supabase
      .from('items')
      .select('id, item_number, description, vendor')
      .eq('item_number', n)
      .limit(1);
    if (data && data.length) {
      const it = data[0];
      updateLine(id, { item_id: it.id, description: it.description || '', vendor: it.vendor || '', match_status: 'matched' });
    } else {
      updateLine(id, { item_id: null, match_status: 'manual' });
    }
  }

  // If the Description cell is edited to an item number, resolve it the same way:
  // look it up in the catalog and replace the row with the matched item.
  async function resolveDescriptionAsItemNumber(id, text) {
    const t = (text || '').trim();
    if (!t) return;
    const candidates = Array.from(new Set([t, t.split(/\s+/)[0]].filter(Boolean)));
    const { data } = await supabase
      .from('items')
      .select('id, item_number, description, vendor')
      .in('item_number', candidates)
      .limit(2);
    if (data && data.length) {
      const it = data[0];
      updateLine(id, { item_id: it.id, item_number: it.item_number, description: it.description || '', vendor: it.vendor || '', match_status: 'matched' });
    }
  }
  function removeLine(id) { setLines(ls => ls.filter(l => l._id !== id)); }
  function addLine() { setLines(ls => [...ls, newLine()]); }

  // Best-effort catalog matching by description keyword search.
  function suggestMatches() { return lookUpByDescription(lines); }

  async function lookUpByDescription(rows) {
    setMatching(true);
    const run = async (l) => {
      if (l.item_id || !l.description) return l;
      const words = l.description.split(/[^A-Za-z0-9]+/).filter(w => w.length >= 4).sort((a, b) => b.length - a.length).slice(0, 1);
      if (!words.length) return l;
      const { data } = await supabase
        .from('items')
        .select('id, item_number, description, vendor')
        .ilike('description', `%${words[0]}%`)
        .limit(8);
      if (!data || !data.length) return { ...l, match_status: 'unmatched' };
      const best = pickBest(l.client_text || l.description, data);
      // Description is taken from the system (catalog) once an item is matched.
      return best ? { ...l, item_id: best.id, item_number: best.item_number, vendor: best.vendor || '', description: best.description || l.description, match_status: 'unverified' } : l;
    };
    // cap concurrency at 8
    const out = [];
    for (let i = 0; i < rows.length; i += 8) {
      out.push(...await Promise.all(rows.slice(i, i + 8).map(run)));
    }
    setLines(out);
    setMatching(false);
    /*
      **Only said when there is something to say.** Every line carries its own
      answer on the line itself, so a banner repeating the tally is noise. What
      it is worth saying out loud is the case where nothing was found at all,
      because that usually means the catalog has not been loaded rather than
      that the order is unusual.
    */
    const found = out.filter(l => l.item_id).length;
    if (!found && out.length) {
      setMsg({ tone: 'warn', text: 'None of these matched anything in the item catalog. '
        + 'Type an item number on a line to set it yourself.' });
    }
  }

  function resetForm() {
    setClientType('external'); setClientName(''); setEntityId(''); setContactEmail('');
    setNotes(''); setRawText(''); setFileName(''); setLines([]); setMsg(null);
  }

  async function saveOrder() {
    if (!clientName.trim()) { setMsg({ tone: 'err', text: 'Enter a client name first.' }); return; }
    if (!lines.length) { setMsg({ tone: 'err', text: 'Add at least one line item.' }); return; }
    setSaving(true); setMsg(null);
    const order = {
      client_name: clientName.trim(),
      client_type: clientType,
      referral_source_id: clientType === 'referral_source' ? entityId || null : null,
      account_id: clientType === 'account' ? entityId || null : null,
      contact_email: contactEmail.trim() || null,
      source_format: fileName ? (/\.csv$/i.test(fileName) ? 'csv' : 'excel') : (rawText ? 'email_text' : 'manual'),
      raw_input: rawText || null,
      notes: notes.trim() || null,
      status: 'draft',
      created_by: profile?.id || null,
    };
    const { data: inserted, error } = await supabase.from('client_orders').insert(order).select('id').single();
    if (error) { setSaving(false); setMsg({ tone: 'err', text: 'Save failed: ' + error.message }); return; }
    const rows = lines.map((l, i) => ({
      client_order_id: inserted.id, line_no: i + 1, raw_text: l.raw_text || null,
      description: l.description || null, quantity: Number(l.quantity) || null,
      item_id: l.item_id || null, item_number: l.item_number || null, vendor: l.vendor || null,
      match_status: l.match_status || 'unmatched',
    }));
    const { error: lineErr } = await supabase.from('client_order_lines').insert(rows);
    setSaving(false);
    if (lineErr) { setMsg({ tone: 'err', text: 'Saved order but lines failed: ' + lineErr.message }); return; }
    resetForm();
    setMsg({ tone: 'ok', text: 'Order saved.', mark: 'delivered' });
    setTab('orders');
  }

  function download(filename, text) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }
  const safeName = () => (clientName || 'order').replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  // Export in the ClaimSoft Transfer Order format so it feeds the Replenishment
  // flow. Only the columns you use are populated (Source/Destination warehouse,
  // ItemNumber, Description, QuantityOrdered, QuantityReceived=0); the rest are
  // left blank for the system to fill on import.
  function exportTransferOrder() {
    const csv = buildTransferOrderCsv(lines, {
      sourceWarehouse: sourceWh,
      destinationWarehouse: destWh,
    });
    download(`transfer-order-${safeName()}.csv`, csv);
  }

  // A simpler review export with match status + original text (for your own records).
  function exportReviewCsv() {
    const esc = (v) => '"' + String(v ?? '').replace(/"/g, '""') + '"';
    const header = ['Line', 'Item Number', 'Description', 'Vendor', 'Quantity', 'Match Status', 'Original Text'];
    const body = lines.map((l, i) => [i + 1, l.item_number, l.description, l.vendor, l.quantity, l.match_status, l.raw_text].map(esc).join(','));
    const meta = `Client,${esc(clientName)}\nType,${esc(activeType.label)}\nDate,${esc(new Date().toLocaleString())}\n\n`;
    download(`client-order-${safeName()}.csv`, meta + header.map(esc).join(',') + '\n' + body.join('\n'));
  }

  // ---- render -------------------------------------------------------------
  // New Order / Orders are sub-page navigation, so they become sub-tabs in the
  // header's control tier rather than a pair of buttons beside the title.
  /*
    **An opened order names itself in the title**, the same correction the
    Replenishment screen needed on the same day: a subtitle is hidden below
    1280, so a phone would show "Client Ordering" with no idea which order was
    open.
  */
  const header = (
    <PageBar
      title={openOrder ? openOrder.order.client_name
        : tab === 'new' && lines.length ? 'A new order' : 'Client Ordering'}
      subtitle={openOrder
        ? undefined
        : 'Turn what a client sent into clean, matched lines'}
      subTabs={openOrder ? undefined : [{ key: 'new', label: 'New Order' }, { key: 'orders', label: 'Orders' }]}
      activeSubTab={tab}
      onSubTabChange={setTab}
      meta={openOrder
        ? openOrder.lines.length + (openOrder.lines.length === 1 ? ' line' : ' lines')
        : tab === 'new'
          ? (lines.length ? lines.length + (lines.length === 1 ? ' line' : ' lines') : undefined)
          : (orders.length ? shownOrders.length + (shownOrders.length === 1 ? ' order' : ' orders') : undefined)}
      /* The search the drawing puts on the orders list, and never had. */
      controls={!openOrder && tab === 'orders' && orders.length ? (
        <input className="tb-search" placeholder="Search a client"
          value={orderSearch} onChange={e => setOrderSearch(e.target.value)} />
      ) : undefined}
      /* Its two filters, behind the Filters button so a phone gets one chip
         rather than a row each. */
      filterCount={!openOrder && tab === 'orders'
        ? (orderKind !== 'all' ? 1 : 0) + (orderWhen !== 'all' ? 1 : 0) : 0}
      drawer={!openOrder && tab === 'orders' && orders.length ? (
        <>
          {orderKinds.length > 1 && (
            <>
              <span className="tb-lbl">Kind</span>
              <select className={'tb-select' + (orderKind !== 'all' ? ' set' : '')}
                value={orderKind} onChange={e => setOrderKind(e.target.value)}>
                <option value="all">All kinds</option>
                {orderKinds.map(k => (
                  <option key={k} value={k}>
                    {CLIENT_TYPES.find(t => t.key === k)?.label || k}
                  </option>
                ))}
              </select>
            </>
          )}
          <span className="tb-lbl">When</span>
          <select className={'tb-select' + (orderWhen !== 'all' ? ' set' : '')}
            value={orderWhen} onChange={e => setOrderWhen(e.target.value)}>
            <option value="all">Any date</option>
            {Object.entries(WITHIN).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          {(orderKind !== 'all' || orderWhen !== 'all') && (
            <button className="tb-btn tb-btn-danger"
              onClick={() => { setOrderKind('all'); setOrderWhen('all'); }}>
              Clear filters
            </button>
          )}
        </>
      ) : undefined}
      /*
        **The three acts belong in the bar**, which is Group G's drawing and
        rule 26. They were scattered: two in a card's heading, halfway down the
        page, and Save at the very bottom under the last line of a table that
        can be four hundred rows long.
      */
      actions={openOrder
        ? <button className="tb-btn tb-btn-ghost" onClick={() => setOpenOrder(null)}>Back to orders</button>
        : tab === 'new' && lines.length ? (
          <button className="tb-btn tb-btn-primary" onClick={saveOrder} disabled={saving}>
            {saving ? 'Saving...' : 'Save the order'}
          </button>
        ) : undefined}
      /* The two files this can make are the quiet acts. Saving the order is
         what you came for, and it is the one that never folds. */
      quiet={!openOrder && tab === 'new' && lines.length ? (
        <>
          <button className="tb-btn tb-btn-ghost" onClick={exportTransferOrder}>Make a transfer order</button>
          <button className="tb-btn tb-btn-ghost" onClick={exportReviewCsv}>A copy for your records</button>
        </>
      ) : undefined}
    />
  );

  return (
    <div>
      {header}

      {msg && (
        <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 'var(--oh-radius-control)', fontSize: 13,
          background: msg.tone === 'ok' ? C.okBg : msg.tone === 'err' ? 'var(--critical-bg)' : 'var(--warning-bg)',
          border: `1px solid ${msg.tone === 'ok' ? C.okLine : msg.tone === 'err' ? 'var(--critical-bg)' : 'var(--warning-bg)'}`,
          color: msg.tone === 'ok' ? C.okInk : msg.tone === 'err' ? C.errInk : C.warnInk,
          display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* The logo's success drop when an order goes through, rebrand step 2
              (guide 3.7). */}
          {msg.mark === 'delivered' && <OnHandIcon size={28} animate="delivered" decorative />}
          <span>{msg.text}</span>
        </div>
      )}

      {tab === 'new' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Client */}
          <Card title="Who the order is for">
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {/*
                **Fault D, and the approved answer to it.** Two of the four
                client types point at lists with nothing in them: referral
                sources and accounts are both empty. **Offering two empty
                dropdowns with nothing to explain them is worse than saying
                so**, which is what the drawing settles: every type keeps its
                place and says how many it can find, and a sentence underneath
                names the empty ones and what would fill them.

                It also stops being a dropdown. Four choices, each carrying a
                number, is a row of chips: the number is the point and a
                dropdown hides it until you open it.
              */}
              <Field label="" full>
                <div className="ct-chips">
                  {CLIENT_TYPES.map(t => {
                    const n = t.entity ? (entities[t.entity] || []).length : null;
                    const empty = t.entity && n === 0;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        className={'ct-chip' + (t.key === clientType ? ' on' : '') + (empty ? ' empty' : '')}
                        onClick={() => { setClientType(t.key); setEntityId(''); }}
                      >
                        {t.label}
                        {t.entity && (
                          <span className="ct-chip-n">{empty ? 'none yet' : n.toLocaleString()}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {emptyTypeNote && <span className="ct-note">{emptyTypeNote}</span>}
              </Field>
              {activeType.entity && (entities[activeType.entity] || []).length > 0 ? (
                <Field label={activeType.label} grow>
                  <select className="select" value={entityId} onChange={e => onPickEntity(e.target.value)} style={{ minWidth: 240 }}>
                    <option value="">Choose one</option>
                    {(entities[activeType.entity] || []).map(e => (
                      <option key={e.id} value={e.id}>{e.display_name || e.name}</option>
                    ))}
                  </select>
                </Field>
              ) : null}
              {/*
                **The two boxes overlapped on a phone**, found 8 September 2026
                by opening this screen at 390: the labels sat side by side and
                "Client / customer n" ran straight into "Optional". They were in
                a wrapping row with a minimum width that a 390 pixel screen
                cannot give two of, so they shrank into each other instead of
                stacking. `.mv-setup-row` is the shape the rest of Ordering uses
                for exactly this pair of boxes.
              */}
              <div className="mv-setup-row" style={{ width: '100%' }}>
              <Field label="Client name" grow>
                <input className="input" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client / customer name" />
              </Field>
              {/* The drawing's words. "Contact email" is what the column is
                  called; this is what it is for. */}
              <Field label="Where the answer goes" grow>
                <input className="input" value={contactEmail} onChange={e => setContactEmail(e.target.value)}
                  placeholder="Optional" />
              </Field>
              </div>
            </div>
          </Card>

          {/*
            -- The order, as Group G's approved drawing has it --------------

            **Two columns, side by side: what the client sent, and what OnHand
            made of it.** They were two separate cards with a third below them,
            so the thing you are checking and the thing you are checking it
            against were never on screen together. That is the whole job of this
            screen.

            **The lines the parser did not take are greyed**, with a sentence
            saying so. Writing that found two lines it WAS taking and should not
            have been, on the drawing's own example email: the sentence
            introducing the order, and the name of the person who sent it. Every
            order made from a pasted email has carried them.

            Below 1024 the two columns become one, in the order a person works:
            what came in first, what OnHand made of it under it.
          */}
          <Card
            title="The order"
            subtitle={lines.length
              ? `${lines.length} ${lines.length === 1 ? 'line' : 'lines'}, ${unitsTotal} `
                + `${unitsTotal === 1 ? 'unit' : 'units'}, from ${fileName ? fileName : 'a pasted email'}.`
              : 'Paste what the client sent, or upload the file they sent.'}
          >
            <div className="co-two">
              <div className="co-side">
                <div className="co-side-t">What the client sent</div>
                {read.length ? (
                  <>
                    <div className="co-raw">
                      {read.map((l, i) => (
                        <div key={i} className={'co-line' + (l.used ? '' : ' out')}>
                          {l.blank ? '\u00a0' : l.text}
                        </div>
                      ))}
                    </div>
                    <div className="ct-note">
                      The greyed lines are not order lines and are left out. A greeting, a sign-off
                      and whoever signed it are not things anybody is ordering.
                      <button className="co-again" onClick={() => { setLines([]); setFileName(''); }}>
                        Start again
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <textarea
                      className="input" value={rawText} onChange={e => setRawText(e.target.value)}
                      placeholder={"Paste the client's order here, for example\n\nHi Jake,\n\n2x Walking Boot - Air - Standard - Small\nCervical Collar Vista qty 3\n- Knee Immobilizer 18\" x 4"}
                      style={{ width: '100%', minHeight: 190, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    <div className="co-intake">
                      {/* "Read the order", not "Standardize pasted text". Nobody
                          outside a database says standardize. */}
                      <button className="btn btn-primary" onClick={standardizeText} disabled={!rawText.trim()}>
                        Read the order
                      </button>
                      <span style={{ color: C.dim, fontSize: 12.5 }}>or</span>
                      <label className="btn btn-quiet" style={{ cursor: 'pointer', margin: 0 }}>
                        Upload the file they sent
                        <input type="file" accept=".xlsx,.xls,.csv,text/csv" style={{ display: 'none' }}
                          onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
                      </label>
                    </div>
                  </>
                )}
              </div>

              <div className="co-side">
                <div className="co-side-t">What OnHand made of it</div>
                {lines.length === 0 ? (
                  <div className="ct-note" style={{ marginTop: 0 }}>
                    Nothing yet. Read an order on the left, or add a line by hand.
                    <span style={{ display: 'block', marginTop: 10 }}>
                      <button className="btn btn-quiet" onClick={addLine}>+ Add a line</button>
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="co-lines">
                      {lines.map(l => (
                        <div key={l._id} className={'co-line-card ' + toneOf(l.match_status)}>
                          <div className="co-line-top">
                            <input className={CELL_CLASS} style={{ ...cellInputStyle, flex: 1, minWidth: 0, fontWeight: 700 }}
                              value={l.item_number} placeholder="No item number yet"
                              onChange={e => updateLine(l._id, { item_number: e.target.value, match_status: e.target.value ? 'manual' : 'unmatched', item_id: null })}
                              onBlur={e => resolveItemNumber(l._id, e.target.value)} />
                            <span className="co-qty">
                              <input className={CELL_CLASS} type="number" min="0" value={l.quantity}
                                style={{ ...cellInputStyle, width: 62, textAlign: 'right' }}
                                onChange={e => updateLine(l._id, { quantity: e.target.value })} />
                              <span className="co-qty-l">Qty</span>
                            </span>
                            <button className="co-drop" aria-label="Take this line out"
                              onClick={() => removeLine(l._id)}><Close size={14} /></button>
                          </div>
                          <input className={CELL_CLASS} style={{ ...cellInputStyle, marginTop: 7 }}
                            value={l.description}
                            onChange={e => updateLine(l._id, { description: e.target.value })}
                            onBlur={e => resolveDescriptionAsItemNumber(l._id, e.target.value)} />
                          <div className="co-line-foot">
                            {l.client_text && l.client_text !== l.description && (
                              <span className="mv-written">{'\u201c' + l.client_text + '\u201d'}</span>
                            )}
                            <MatchPill status={l.match_status} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="co-intake">
                      <button className="btn btn-quiet" onClick={addLine}>+ Add a line</button>
                      <button className="btn btn-quiet" onClick={suggestMatches} disabled={matching}>
                        {matching ? 'Looking...' : 'Look the rest up in the catalog'}
                      </button>
                    </div>
                    <div className="ct-note">
                      A line that matched a catalog item carries its number. One that did not says so
                      and waits for you to pick one.
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>

          {/*
            **Where a transfer order would ship from and to.** Only worth asking
            once there is something to ship, which is why it is not on the
            drawing's empty state.
          */}
          {lines.length > 0 && (
            <Card title="If you make a transfer order out of this"
              subtitle="Both are optional, and both go on every row of the file.">
              <div className="mv-setup-row ord-when">
                <Field label="Ships from">
                  <input className="input" list="wh-list" value={sourceWh}
                    onChange={e => setSourceWh(e.target.value)} placeholder="Optional" />
                </Field>
                <Field label="Ships to">
                  <input className="input" list="wh-list" value={destWh}
                    onChange={e => setDestWh(e.target.value)} placeholder="Optional" />
                </Field>
                <datalist id="wh-list">
                  {(entities.warehouses || []).map(w => <option key={w.id} value={w.display_name || w.name} />)}
                </datalist>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <>
          {loadingOrders ? (
            <PageLoading rows={5} />
          ) : openOrder ? (
            /*
              -- One order, opened ------------------------------------------

              **No row on this list opened until 8 September 2026**, so a saved
              order could never be looked at again: the lines it was made of,
              what each one matched to, and what the client actually sent were
              all written down and unreachable.

              Read only, like a saved replenishment run: the order has been sent
              and offering an edit that goes nowhere would be a lie.
            */
            <div>
              <Figures>
                <Figure n={openOrder.lines.length} label="Lines" />
                <Figure n={openOrder.lines.reduce((a, l) => a + (Number(l.quantity) || 0), 0)} label="Units" />
                <Figure
                  n={openOrder.lines.filter(l => l.match_status && l.match_status !== 'matched').length}
                  label="Not matched"
                  tone={openOrder.lines.some(l => l.match_status && l.match_status !== 'matched') ? 'var(--critical-ink)' : undefined}
                />
                {/* Rule 5's strip is numbers. "Email" in a number's place read
                    as a mistake, so how it arrived moved down beside what
                    actually arrived, where it belongs anyway. */}
                <Figure n={openOrder.lines.filter(l => l.match_status === 'matched').length} label="Matched" />
              </Figures>

              {/* The order keeps what the client sent as `raw_input`; `raw_text`
                  is each LINE's own words. This read the line's name off the
                  order, so an opened order never showed what the client sent
                  (the audit after the rebrand, issue 17, 18 September 2026). */}
              {openOrder.order.raw_input && (
                <div className="card mv-file" style={{ display: 'block' }}>
                  {/* It read "What the client sent, by email_text", which is a
                      column name printed at a person. */}
                  <span className="mv-file-label">
                    What the client sent
                    {openOrder.order.source_format && <>, as {arrivedAs(openOrder.order.source_format)}</>}
                  </span>
                  <pre className="co-raw">{openOrder.order.raw_input}</pre>
                </div>
              )}

              <div className="rplist">
                <ListGrid
                  columns={[
                    { label: 'Item number', width: 'minmax(0,1fr)' },
                    { label: 'Description', width: 'minmax(0,2fr)' },
                    { label: 'What the client wrote', width: 'minmax(0,1.4fr)' },
                    { label: 'Qty', width: '90px', align: 'right' },
                    { label: 'Matched', width: '150px' },
                  ]}
                  rows={openOrder.lines}
                  keyOf={l => l.id}
                  cells={l => [
                    <span className="mv-item">{l.item_number || <span style={{ color: 'var(--text-secondary)' }}>None</span>}</span>,
                    l.description ? <span>{l.description}</span>
                                  : <span style={{ color: 'var(--text-secondary)' }}>No description</span>,
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>{l.raw_text || ''}</span>,
                    <span>{l.quantity ?? ''}</span>,
                    <MatchPill status={l.match_status} />,
                  ]}
                  stack={l => (
                    <span>
                      <span className="mv-item" style={{ display: 'block' }}>{l.item_number || 'No item number'}</span>
                      <span style={{ display: 'block', fontSize: 13, marginTop: 2 }}>
                        {l.description || <span style={{ color: 'var(--text-secondary)' }}>No description</span>}
                      </span>
                      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                        Written "{l.raw_text || ''}"{l.quantity ? ' \u00b7 ' + l.quantity : ''}
                      </span>
                      <span style={{ display: 'block', marginTop: 8 }}><MatchPill status={l.match_status} /></span>
                    </span>
                  )}
                />
              </div>
            </div>
          ) : orders.length === 0 ? (
            <EmptyScreen
              title="No client orders yet"
              says={'An order starts as an email or a spreadsheet from a client. OnHand turns what '
                  + 'they sent into clean lines, matches each one to the catalog, and keeps what it '
                  + 'made of it here, so an order can be looked at again and turned into a transfer.'}
              action={{ label: 'New order', onClick: () => setTab('new') }}
            />
          ) : (
            /* Rule 15: a grid, not an HTML table. And its rows open. */
            <div className="rplist">
              {shownOrders.length === 0 && (
                <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                  No order matches what you are looking for.
                </div>
              )}
              {shownOrders.length > 0 && (
              <ListGrid
                /* The drawing's own words for these six. "For" and "Came in as"
                   and "Created" were this screen's; "Kind", "Arrived as" and
                   "Saved" are the ones on `orders-list-1440.png`. */
                columns={[
                  { label: 'Client', width: 'minmax(0,1.4fr)' },
                  { label: 'Kind', width: 'minmax(0,1fr)' },
                  { label: 'Lines', width: '90px', align: 'right' },
                  { label: 'Arrived as', width: '150px' },
                  { label: 'Status', width: '120px' },
                  { label: 'Saved', width: '170px' },
                ]}
                rows={shownOrders}
                keyOf={o => o.id}
                onRowClick={o => openSavedOrder(o.id)}
                cells={o => [
                  <span className="mv-run-name">{o.client_name}</span>,
                  <span>{CLIENT_TYPES.find(t => t.key === o.client_type)?.label || o.client_type}</span>,
                  <span>{o.client_order_lines?.[0]?.count ?? 0}</span>,
                  <span>{arrivedAs(o.source_format)}</span>,
                  <StatusPill status={o.status} />,
                  <span style={{ color: 'var(--text-secondary)' }}>{dayShortAndClock(o.created_at)}</span>,
                ]}
                stack={o => (
                  <span>
                    <span className="mv-run-name" style={{ display: 'block' }}>{o.client_name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>
                      {CLIENT_TYPES.find(t => t.key === o.client_type)?.label || o.client_type}
                      {' \u00b7 '}{o.client_order_lines?.[0]?.count ?? 0} lines
                    </span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {arrivedAs(o.source_format)} - saved {dayShortAndClock(o.created_at)}
                    </span>
                    <span style={{ display: 'block', marginTop: 8 }}><StatusPill status={o.status} /></span>
                  </span>
                )}
              />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function Card({ title, subtitle, right, children }) {
  return (
    <div style={{ background: 'var(--surface-card)', border: `1px solid ${C.line}`, borderRadius: 12, padding: 16, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

/*
  `full` takes a whole row rather than sharing one. Added 8 September 2026 for
  the four client-type chips, which the approved drawing puts on their own line
  above the name and the email: sharing the row wrapped them onto two lines at
  1440 and read as a mistake.
*/
/*
  One labelled box.

  **`grow` means "share the row, and take a line of your own when there is not
  enough of it"**, corrected 8 September 2026. It meant `flex: 1`, which has no
  width below which it gives up, so on a 390 pixel screen the client's name and
  the email address sat side by side and shrank into each other: the picture
  read "Client / cust" beside "Optional", with the two labels touching.

  A basis of 240 is what the rest of Ordering uses for exactly this pair.
*/
function Field({ label, children, grow, full }) {
  return (
    <div style={{ flex: full ? '1 1 100%' : grow ? '1 1 240px' : '0 1 240px', minWidth: 0 }}>
      {label && <label className="input-label">{label}</label>}
      {children}
    </div>
  );
}

/*
  The colour down a line's left edge, from what the catalog said about it. The
  same three tones every list in Ordering and Movement uses, so a line that
  needs a person looks the same wherever it is drawn.
*/
function toneOf(status) {
  if (status === 'matched') return 'ok';
  if (status === 'unmatched') return 'bad';
  return 'warn';
}

function MatchPill({ status }) {
  const map = {
    matched:   { c: C.okInk, bg: C.okBg, t: 'Matched' },
    unverified:{ c: C.warnInk, bg: 'var(--warning-bg)', t: 'Verify' },
    manual:    { c: C.blue, bg: C.blueBg, t: 'Manual' },
    unmatched: { c: C.dim, bg: 'var(--hover)', t: 'No match' },
  };
  const s = map[status] || map.unmatched;
  return <span style={{ fontSize: 11, fontWeight: 700, color: s.c, background: s.bg, padding: '2px 8px', borderRadius: 'var(--oh-radius-chip)' }}>{s.t}</span>;
}

/*
  How the order arrived, in the drawing's words. `source_format` holds
  `email_text`, `excel`, `csv` or `manual`, and the screen printed those
  straight out: a row read "email_text", which is a column name.
*/
function arrivedAs(format) {
  const map = {
    email_text: 'Pasted email',
    email: 'Pasted email',
    excel: 'Excel',
    xlsx: 'Excel',
    csv: 'CSV',
    manual: 'Typed in here',
  };
  const key = String(format || '').trim().toLowerCase();
  if (map[key]) return map[key];
  /*
    **An unknown value is shown, not renamed.** The first version of this fell
    back to "Typed in here", so three orders that arrived as an email and a
    spreadsheet all read as typed in by hand: a definite, wrong answer, which is
    worse than an unfamiliar word. Rule 9.
  */
  if (key) return format;
  return <span style={{ color: 'var(--text-secondary)' }}>Not recorded</span>;
}

function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', color: s.c, background: s.bg, padding: '2px 8px', borderRadius: 'var(--oh-radius-chip)' }}>{status}</span>;
}
